import { Box, FormLabel, Input, SimpleGrid, Text, Flex } from '@chakra-ui/react'
import React, { useState, useEffect } from 'react'

interface ImplantsSectionProps {
  currentImplants: string
  setCurrentImplants: (value: string) => void
  implantTotalTeeth: string
  setImplantTotalTeeth: (value: string) => void
  firstDentalImplantAge: string
  setFirstDentalImplantAge: (value: string) => void
  lastDentalImplantAge: string
  setLastDentalImplantAge: (value: string) => void
  computedImplantYearsExposed: string | number
  computedImplantExposure: string | number
  isSaved: boolean
  adjustedImplants?: string
  adjustedTotalImplants?: string
  hasImplantAdjustments?: boolean
  hasTotalImplantAdjustments?: boolean
}

const ImplantsSection: React.FC<ImplantsSectionProps> = ({
  currentImplants,
  setCurrentImplants,
  implantTotalTeeth,
  setImplantTotalTeeth,
  firstDentalImplantAge,
  setFirstDentalImplantAge,
  lastDentalImplantAge,
  setLastDentalImplantAge,
  computedImplantYearsExposed,
  computedImplantExposure,
  isSaved,
  adjustedImplants,
  adjustedTotalImplants,
  hasImplantAdjustments,
  hasTotalImplantAdjustments,
}) => {
  // Helper function to determine if a field should be disabled
  // Only disable fields when the form has been saved AND the field has a value
  const isFieldDisabled = (value: string) => {
    return isSaved && value !== ''
  }

  // Internal state for handling input changes
  const [workingCurrentImplants, setWorkingCurrentImplants] =
    useState(currentImplants)
  const [workingImplantTotalTeeth, setWorkingImplantTotalTeeth] =
    useState(implantTotalTeeth)
  const [workingFirstDentalImplantAge, setWorkingFirstDentalImplantAge] =
    useState(firstDentalImplantAge)
  const [workingLastDentalImplantAge, setWorkingLastDentalImplantAge] =
    useState(lastDentalImplantAge)

  // Update internal state when props change (for initial load and adjustments)
  useEffect(() => {
    if (isSaved && adjustedImplants) {
      setWorkingCurrentImplants(adjustedImplants)
    } else {
      setWorkingCurrentImplants(currentImplants)
    }
  }, [isSaved, currentImplants, adjustedImplants])

  useEffect(() => {
    if (isSaved && adjustedTotalImplants) {
      setWorkingImplantTotalTeeth(adjustedTotalImplants)
    } else {
      setWorkingImplantTotalTeeth(implantTotalTeeth)
    }
  }, [isSaved, implantTotalTeeth, adjustedTotalImplants])

  useEffect(() => {
    setWorkingFirstDentalImplantAge(firstDentalImplantAge)
  }, [firstDentalImplantAge])

  useEffect(() => {
    setWorkingLastDentalImplantAge(lastDentalImplantAge)
  }, [lastDentalImplantAge])

  return (
    <>
      <Text fontSize="lg" fontWeight="bold" mt={8}>
        Implants
      </Text>
      <SimpleGrid columns={1} spacing={4} mt={4}>
        <Flex>
          <FormLabel w="50%">1. Current number of dental implants</FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter current number"
              value={workingCurrentImplants}
              onChange={(e) => {
                if (!isFieldDisabled(currentImplants)) {
                  setWorkingCurrentImplants(e.target.value)
                }
              }}
              onBlur={() => setCurrentImplants(workingCurrentImplants)}
              disabled={isFieldDisabled(currentImplants)}
            />
            {isSaved && hasImplantAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            2. The number teeth with dental implants in your life
          </FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter total number"
              value={workingImplantTotalTeeth}
              onChange={(e) => {
                if (!isFieldDisabled(implantTotalTeeth)) {
                  setWorkingImplantTotalTeeth(e.target.value)
                }
              }}
              onBlur={() => setImplantTotalTeeth(workingImplantTotalTeeth)}
              disabled={isFieldDisabled(implantTotalTeeth)}
            />
            {isSaved && hasTotalImplantAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            3. Estimated age you had 1st dental implant placed
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingFirstDentalImplantAge}
            onChange={(e) => {
              if (!isFieldDisabled(firstDentalImplantAge)) {
                setWorkingFirstDentalImplantAge(e.target.value)
              }
            }}
            onBlur={() =>
              setFirstDentalImplantAge(workingFirstDentalImplantAge)
            }
            disabled={isFieldDisabled(firstDentalImplantAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            4. Age you had your last dental implant removed (if still have
            implants leave blank)
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingLastDentalImplantAge}
            onChange={(e) => {
              if (!isFieldDisabled(lastDentalImplantAge)) {
                setWorkingLastDentalImplantAge(e.target.value)
              }
            }}
            onBlur={() => setLastDentalImplantAge(workingLastDentalImplantAge)}
            disabled={isFieldDisabled(lastDentalImplantAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Number of years exposed</em>
          </FormLabel>
          <Text w="25%" fontWeight="bold" pl={3}>
            {computedImplantYearsExposed || '-'}
          </Text>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Estimated exposure</em>
          </FormLabel>
          <Text w="25%" fontWeight="bold" pl={3}>
            {computedImplantExposure || '-'}
          </Text>
        </Flex>
      </SimpleGrid>
    </>
  )
}

export default ImplantsSection
